/**
 * VoiceInput — Microphone toggle button
 *
 * Two modes, selectable via the `mode` prop:
 *   - 'server'  (default): record with MediaRecorder → POST to /api/transcribe
 *                          (Sarvam primary, Whisper fallback via provider module).
 *                          This is the path tested against warehouse Hinglish.
 *   - 'browser'          : Web Speech API (kept because it works offline).
 *
 * The `lang` prop is used for the browser path only; the server path derives
 * language server-side (en-IN for saaras:v4).
 */
import React, { useState, useRef, useCallback } from 'react';
import { Mic, MicOff } from 'lucide-react';

const RAW_API_URL = process.env.REACT_APP_API_URL || 'https://skilldelivery-platform.onrender.com/api';
const API_URL = RAW_API_URL.endsWith('/api') ? RAW_API_URL : `${RAW_API_URL.replace(/\/$/, '')}/api`;

// Minimum thresholds — see 2026-08-04 truncation bug notes.
// Under these values Sarvam returns empty and Whisper hallucinates "you".
const MIN_RECORDING_MS = 1000;
const MIN_BLOB_BYTES = 2000;
const RECORDING_TIMESLICE_MS = 1000; // fires ondataavailable every second
const TOO_SHORT_MSG = 'Recording too short — please hold the button and speak for at least two seconds.';

const supportsMediaRecorder = () =>
  typeof window !== 'undefined' &&
  typeof window.MediaRecorder !== 'undefined' &&
  !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);

const supportsWebSpeech = () =>
  typeof window !== 'undefined' &&
  (window.SpeechRecognition || window.webkitSpeechRecognition);

const VoiceInput = ({ onTranscript, onInterimText, disabled = false, lang = 'hi-IN', mode = 'server' }) => {
  const [isListening, setIsListening] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const recognitionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const mediaStreamRef = useRef(null);
  const recordStartedAtRef = useRef(0);

  // Effective mode: 'server' if requested and supported; else fall back to
  // 'browser' if that works; else the button hides itself.
  const effectiveMode = (mode === 'server' && supportsMediaRecorder())
    ? 'server'
    : (supportsWebSpeech() ? 'browser' : null);

  // ── Browser (Web Speech API) path — unchanged behaviour ──
  const stopBrowser = useCallback(() => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (e) { /* ignore */ }
      recognitionRef.current = null;
    }
    setIsListening(false);
    if (onInterimText) onInterimText('');
  }, [onInterimText]);

  const startBrowser = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Voice input requires Google Chrome browser.');
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = lang;
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognitionRef.current = recognition;

    recognition.onstart = () => {
      console.log('[Voice] Browser mode — recognition started, lang:', lang);
      setIsListening(true);
    };

    recognition.onresult = (event) => {
      const last = event.results.length - 1;
      const transcript = event.results[last][0].transcript;
      const isFinal = event.results[last].isFinal;
      if (isFinal) {
        if (onInterimText) onInterimText('');
        if (onTranscript) onTranscript(transcript);
        recognitionRef.current = null;
        setIsListening(false);
      } else if (onInterimText) {
        onInterimText(transcript);
      }
    };

    recognition.onerror = (event) => {
      console.error('[Voice] Browser error:', event.error);
      recognitionRef.current = null;
      setIsListening(false);
      if (onInterimText) onInterimText('');
      if (event.error === 'not-allowed') {
        alert('Microphone permission denied. Please click the lock icon in the address bar → Site settings → Microphone → Allow');
      }
    };

    recognition.onend = () => {
      recognitionRef.current = null;
      setIsListening(false);
    };

    try { recognition.start(); }
    catch (err) {
      console.error('[Voice] Failed to start:', err);
      recognitionRef.current = null;
      setIsListening(false);
    }
  }, [lang, onTranscript, onInterimText]);

  // ── Server (MediaRecorder + /api/transcribe) path ──
  const cleanupStream = () => {
    if (mediaStreamRef.current) {
      try { mediaStreamRef.current.getTracks().forEach(t => t.stop()); } catch (e) { /* ignore */ }
      mediaStreamRef.current = null;
    }
    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
  };

  const startServer = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      // Prefer webm/opus (Chrome, Edge, Firefox) then ogg (Firefox) then default
      let mimeType = '';
      if (window.MediaRecorder.isTypeSupported && window.MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      } else if (window.MediaRecorder.isTypeSupported && window.MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
        mimeType = 'audio/ogg;codecs=opus';
      } else if (window.MediaRecorder.isTypeSupported && window.MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
      }
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      recorder.onstop = async () => {
        setIsListening(false);

        // Assemble the blob and log diagnostics BEFORE any upload so
        // truncation (a recurring class of bug on this path) is visible.
        const chunks = audioChunksRef.current;
        const type = recorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(chunks, { type });
        audioChunksRef.current = [];
        const elapsedMs = recordStartedAtRef.current ? (Date.now() - recordStartedAtRef.current) : 0;
        recordStartedAtRef.current = 0;
        console.log(`[Voice] onstop assembled: chunks=${chunks.length} size=${blob.size} bytes type="${type}" elapsedMs=${elapsedMs}`);

        // Guard: reject too-short recordings before wasting an API call.
        // Both thresholds share the same user-facing message.
        if (elapsedMs < MIN_RECORDING_MS || blob.size < MIN_BLOB_BYTES) {
          console.warn(`[Voice] Recording rejected as too short (elapsedMs=${elapsedMs}, size=${blob.size})`);
          alert(TOO_SHORT_MSG);
          cleanupStream();
          return;
        }

        setIsUploading(true);
        try {
          const ext = type.includes('ogg') ? 'ogg' : type.includes('webm') ? 'webm' : 'wav';

          const formData = new FormData();
          formData.append('audio', blob, `voice.${ext}`);

          const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
          const res = await fetch(`${API_URL}/transcribe`, {
            method: 'POST',
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
            body: formData
          });
          const data = await res.json().catch(() => ({}));

          if (!res.ok || data.success === false) {
            const msg = data.message || data.error || `Transcription failed (${res.status})`;
            console.warn('[Voice] Server transcription rejected:', msg, data);
            alert(msg);
            return;
          }
          if (data.transcript && onTranscript) {
            console.log(`[Voice] Server transcript (${data.provider}, glossary=${data.glossarySubstitutions || 0}): "${String(data.transcript).substring(0, 80)}"`);
            onTranscript(data.transcript);
          }
        } catch (err) {
          console.error('[Voice] Upload failed:', err);
          alert('Voice upload failed. Please try again.');
        } finally {
          cleanupStream();
          setIsUploading(false);
        }
      };

      // Timeslice so ondataavailable fires every second instead of only at
      // stop — otherwise the final chunk may not be delivered before onstop
      // runs, leaving the assembled blob nearly empty (1-3 KB for a 5s clip).
      recordStartedAtRef.current = Date.now();
      recorder.start(RECORDING_TIMESLICE_MS);
      setIsListening(true);
      console.log(`[Voice] Server mode — recording started, mimeType="${recorder.mimeType}" timeslice=${RECORDING_TIMESLICE_MS}ms`);
    } catch (err) {
      console.error('[Voice] getUserMedia failed:', err);
      if (err && err.name === 'NotAllowedError') {
        alert('Microphone permission denied. Please allow microphone access and try again.');
      } else {
        alert('Voice input unavailable: ' + (err?.message || 'unknown error'));
      }
      cleanupStream();
      setIsListening(false);
    }
  }, [onTranscript]);

  const stopServer = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      cleanupStream();
      setIsListening(false);
      return;
    }
    // Flush any pending buffer before stop(), otherwise the last
    // partial-timeslice chunk may not reach ondataavailable before onstop
    // fires. requestData() forces a synchronous dispatch.
    try { recorder.requestData(); } catch (e) { /* ignore — some browsers throw if inactive */ }
    try { recorder.stop(); } catch (e) { /* ignore */ }
  }, []);

  // ── Public toggle ──
  const toggleVoice = useCallback(() => {
    if (effectiveMode === 'server') {
      if (isListening) stopServer(); else startServer();
    } else if (effectiveMode === 'browser') {
      if (isListening) stopBrowser(); else startBrowser();
    }
  }, [effectiveMode, isListening, startServer, stopServer, startBrowser, stopBrowser]);

  if (!effectiveMode) return null;

  const busy = isUploading;
  const title = isUploading
    ? 'Transcribing…'
    : isListening
      ? (effectiveMode === 'server' ? 'Stop recording (click to send)' : 'Stop listening (click to cancel)')
      : (effectiveMode === 'server' ? 'Voice input — record and send' : 'Voice input — speak in Hindi or English');

  return (
    <button
      onClick={toggleVoice}
      disabled={disabled || busy}
      className={`p-2 rounded-lg transition-all flex-shrink-0 ${
        isListening
          ? 'bg-red-500 text-white shadow-[0_0_0_4px_rgba(239,68,68,0.3)]'
          : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
      } disabled:opacity-50`}
      title={title}
      style={isListening ? { animation: 'micPulse 1.5s infinite' } : undefined}
    >
      {isListening ? (
        <MicOff className="w-5 h-5" />
      ) : (
        <Mic className="w-5 h-5" />
      )}
    </button>
  );
};

export default VoiceInput;
